/* /__ui — the Brightivity App UI Kit rebuilt from src/ui, in the kit's own order and with the
 * kit's own sample content, for side-by-side comparison with
 * app/specifications/michael/Brightivity-Designer-Handoff/…/02-App-UI-Kit/Brightivity-App-UI-Kit.pdf */
import { useState } from 'react';
import {
  Avatar, Button, Card, Chip, ChoicePills, CreditsPill, EmptyState, Icon, ICON_NAMES, IconButton, InkRing, Input, Modal,
  NavItem, OptionCard, Panel, ProgressBar, SearchInput, Segmented, Select, Skeleton, Spinner, StatCard, Tabs, Tag,
  Textarea, Toast, Toggle, ToolTile,
} from '../ui/index.jsx';

const COLORS = [
  { name: 'Navy', hex: '#1B2550', note: 'Sidebar, title bar, text, dark surfaces', fg: '#FFFFFF' },
  { name: 'Sky Blue', hex: '#1AB0F2', note: 'Drag and drop, info, selected states', edge: '#0E8FCC', tint: '#E3F5FD', on: '#0A6A99' },
  { name: 'Coral', hex: '#FF5151', note: 'Main action buttons, alerts, "big print."', edge: '#D93A3A', tint: '#FFE8E8', on: '#C42F2F' },
  { name: 'Sunny Yellow', hex: '#FFBD21', note: 'Active nav item, highlights, tab underline', edge: '#E09A00', tint: '#FFF4DA', on: '#855C00' },
  { name: 'Green', hex: '#26C774', note: 'Success, approve, ready', edge: '#189A57', tint: '#E2F7EC', on: '#117040' },
  { name: 'Purple', hex: '#7F4BF6', note: 'AI features, credits, library', edge: '#5F2FD1', tint: '#EFE8FF', on: '#5F2FD1' },
];
const NEUTRALS = [
  ['Page', '#F4F5F9', 'App background'], ['Surface', '#FFFFFF', 'Cards, panels, inputs'], ['Canvas', '#E9ECF4', 'Editor canvas, outer areas'],
  ['Border', '#E3E6EF', '2 px card and input borders'], ['Fill', '#EEF0F6', 'Count badges, skeletons'], ['Text', '#1B2550', 'Headings and body'],
  ['Muted text', '#545A78', 'Secondary text, labels'], ['Placeholder', '#8A8FA8', 'Placeholder and disabled only'], ['Link', '#0B78AD', 'Text links on white'],
  ['Deep navy', '#0A0F2B', 'Solid edge under navy items'],
];
const TYPE = [
  ['Display', '54px / 900', 'var(--bv-text-display)', 'What are we making today?'],
  ['Page title', '34px / 900', 'var(--bv-text-page-title)', 'My School'],
  ['Section title', '22px / 900', 'var(--bv-text-section)', 'Recent designs'],
  ['Card title', '17px / 900', 'var(--bv-text-card-title)', 'Teach a topic'],
  ['Body', '16px / 700', 'var(--bv-text-body)', 'Approve print requests and manage who can print.'],
  ['Small', '14px / 700', 'var(--bv-text-small)', 'Today, 9:14 AM · 24 × 36 in'],
  ['Label', '13px / 900 / caps +0.12em', null, 'GRADE LEVEL'],
  ['Button', '16px / 900', 'var(--bv-text-button)', 'Preview'],
];

const S = {
  page: { padding: '40px 64px 80px', maxWidth: 1440, margin: '0 auto' },
  h2: { display: 'flex', alignItems: 'baseline', gap: 12, margin: '56px 0 8px', font: 'var(--bv-text-page-title)' },
  num: { font: '900 18px/1 var(--bv-font)', color: 'var(--bv-purple)' },
  lead: { color: 'var(--bv-text-muted)', marginBottom: 20 },
  row: { display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'center', gap: 24, padding: '18px 0', borderTop: '2px solid var(--bv-border)' },
  rowLabel: { font: '900 16px var(--bv-font)' },
  wrap: { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' },
};
const Section = ({ n, title, lead, children }) => (
  <section>
    <h2 style={S.h2}><span style={S.num}>{n}</span>{title}</h2>
    {lead && <p style={S.lead}>{lead}</p>}
    {children}
  </section>
);
const Row = ({ label, children }) => (
  <div style={S.row}><div style={S.rowLabel}>{label}</div><div style={S.wrap}>{children}</div></div>
);

export default function UiKit() {
  const [grade, setGrade] = useState('3–5');
  const [tab, setTab] = useState('Print queue');
  const [seg, setSeg] = useState('yours');
  const [toggle, setToggle] = useState(true);
  const [idea, setIdea] = useState('The water cycle for my 4th graders: evaporation, condensation, precipitation and collection, with a big labeled diagram');
  const [option, setOption] = useState('teach');
  const [modal, setModal] = useState(false);
  return (
    <div style={S.page} data-testid="ui-kit">
      <p className="bv-label">Designer handoff · App UI kit · October 2026</p>
      <h1 style={{ font: 'var(--bv-text-display)', margin: '10px 0 8px' }}>Moving StudioCore to Brightivity</h1>
      <p className="bv-muted">Light, bright and friendly instead of dark and glowing. Same tools and menu, new colors, type, components and screens.</p>

      <Section n="01" title="Color" lead='Six brand colors. Each one has a darker "edge" for the solid shadow under buttons and tiles, a light tint for tags, and a dark text color for use on that tint.'>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }} data-testid="swatches">
          {COLORS.map((c) => (
            <Card key={c.name} pad={false} style={{ overflow: 'hidden' }}>
              <div style={{ background: c.hex, height: 110, padding: 18, color: c.fg || 'var(--bv-navy)', font: '900 20px var(--bv-font)', boxShadow: c.edge ? `inset 0 -7px 0 ${c.edge}` : 'none' }}>{c.name}<div style={{ font: '800 15px var(--bv-font)' }}>{c.hex}</div></div>
              <div style={{ padding: 16 }}>
                <p className="bv-small bv-muted">{c.note}</p>
                {c.edge && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    {[['Edge', c.edge], ['Tint', c.tint], ['On tint', c.on]].map(([k, v]) => (
                      <div key={k} style={{ flex: 1 }}>
                        <div style={{ height: 28, borderRadius: 8, background: v, boxShadow: 'var(--bv-shadow-border)' }} />
                        <div style={{ font: '800 12px var(--bv-font)', marginTop: 4 }}>{k} {v}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
        <p className="bv-label" style={{ margin: '28px 0 12px' }}>Neutrals</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14 }}>
          {NEUTRALS.map(([n, hex, note]) => (
            <div key={n}>
              <div style={{ height: 56, borderRadius: 14, background: hex, boxShadow: 'var(--bv-shadow-border)' }} />
              <div style={{ font: '900 14px var(--bv-font)', marginTop: 6 }}>{n} {hex}</div>
              <div className="bv-small bv-muted">{note}</div>
            </div>
          ))}
        </div>
        <p className="bv-label" style={{ margin: '28px 0 12px' }}>Text on color</p>
        <div style={S.wrap}>
          {[['Navy on Sky Blue', '#1AB0F2', '#1B2550'], ['Navy on Green', '#26C774', '#1B2550'], ['Navy on Yellow', '#FFBD21', '#1B2550'], ['Navy on Coral', '#FF5151', '#1B2550'], ['White on Purple', '#7F4BF6', '#FFFFFF'], ['White on Navy', '#1B2550', '#FFFFFF']].map(([t, bg, fg]) => (
            <span key={t} style={{ background: bg, color: fg, padding: '12px 18px', borderRadius: 14, font: '900 16px var(--bv-font)' }}>{t}</span>
          ))}
        </div>
      </Section>

      <Section n="02" title="Type" lead="Nunito everywhere, free from Google Fonts. Black (900) for titles and buttons, ExtraBold (800) and Bold (700) for everything else. No light weights.">
        <Card>
          {TYPE.map(([n, spec, font, sample]) => (
            <div key={n} style={{ display: 'grid', gridTemplateColumns: '180px 1fr', alignItems: 'baseline', gap: 24, padding: '14px 0', borderBottom: '2px solid var(--bv-border)' }}>
              <div><div style={{ font: '900 16px var(--bv-font)' }}>{n}</div><div className="bv-small bv-muted">{spec}</div></div>
              {font ? <div style={{ font }} data-type={n}>{sample}</div> : <div className="bv-label" data-type={n}>{sample}</div>}
            </div>
          ))}
        </Card>
      </Section>

      <Section n="03" title="Shape and depth" lead="Round and friendly, like the logo. Colored things get a solid darker edge underneath, the same idea as the shadow on the B. White things get a 2 px border instead of a drop shadow.">
        <div style={S.wrap}>
          {[['Pill · 999', 999], ['Tile · 26', 26], ['Card · 24', 24], ['Input · 14', 14], ['Icon tile · 16', 16]].map(([l, r]) => (
            <div key={l} style={{ textAlign: 'center' }}>
              <div style={{ width: 120, height: 72, borderRadius: r, background: 'var(--bv-surface)', boxShadow: 'var(--bv-shadow-border)' }} />
              <div className="bv-small" style={{ marginTop: 6 }}>{l}</div>
            </div>
          ))}
          <div style={{ textAlign: 'center' }}>
            <div style={{ width: 120, height: 72, borderRadius: 24, background: 'var(--bv-surface)', boxShadow: 'var(--bv-shadow-window)' }} />
            <div className="bv-small" style={{ marginTop: 6 }}>Window shadow</div>
          </div>
        </div>
      </Section>

      <Section n="04" title="Components" lead="The building blocks used across every screen. Hover: lift 3 px over 180 ms. Pressed: drop the solid edge to 0. Focus: 3 px purple ring.">
        <Card>
          <Row label="Buttons">
            <Button variant="coral" icon="print" data-testid="btn-coral">Print poster</Button>
            <Button variant="green" icon="check">Approve</Button>
            <Button variant="blue" icon="upload">Upload</Button>
            <Button variant="purple" icon="wand">Restyle</Button>
            <Button variant="secondary" icon="eye">Preview</Button>
            <Button variant="secondary" disabled>Disabled</Button>
          </Row>
          <Row label="Big action"><Button variant="coral" size="big" icon="sparkle">Generate poster</Button></Row>
          <Row label="Chips">
            <Chip selected>All</Chip>
            <Chip dot="yellow" count={3}>Pending</Chip>
            <Chip dot="green" count={5}>Approved</Chip>
            <Chip>A welcome banner for Back-to-School Night</Chip>
          </Row>
          <Row label="Tags">
            <Tag color="blue">Grades 4–6</Tag><Tag color="green">Aligned in 1 state</Tag><Tag color="yellow">Waiting for approval</Tag>
            <Tag color="coral">Print failed</Tag><Tag color="purple">Printables</Tag>
          </Row>
          <Row label="Inputs">
            <SearchInput placeholder="Search printables" style={{ width: 330 }} />
            <Select options={['All regions', 'United States', 'Canada']} style={{ width: 200 }} />
            <Input placeholder="Focused field" style={{ width: 280 }} />
          </Row>
          <Row label="Choice">
            <ChoicePills compact options={['K–2', '3–5', '6–8', '9–12']} value={grade} onChange={setGrade} label="Grade level" />
            <Toggle checked={toggle} onChange={setToggle} />
            <Toggle checked={false} />
          </Row>
          <Row label="Tabs"><Tabs tabs={['Print queue', 'Users', 'Licenses']} value={tab} onChange={setTab} /></Row>
          <Row label="Segmented"><Segmented options={[{ value: 'yours', label: 'Your designs' }, { value: 'canva', label: 'Canva designs' }]} value={seg} onChange={setSeg} /></Row>
          <Row label="Sidebar item">
            <div style={{ display: 'flex', gap: 8, background: 'var(--bv-navy)', padding: 12, borderRadius: 16, width: 230 }}>
              <NavItem icon="home" label="Active" active />
              <NavItem icon="print" label="Default" />
              <NavItem icon="school" label="Badge" badge={3} />
            </div>
          </Row>
          <Row label="Tool tile">
            <ToolTile color="green" icon="browser" title="Web to poster" subtitle="Print any web page" style={{ width: 300, minHeight: 92 }} />
            <span className="bv-small bv-muted" style={{ maxWidth: 330 }}>Big faded icon in the corner, white icon square tilted 6°, navy text on blue, green, yellow and coral tiles, white on purple.</span>
          </Row>
          <Row label="Progress and ink">
            <div style={{ width: 240 }}><ProgressBar value={40} label="Upload" /></div>
            <InkRing channel="C" percent={60} /><InkRing channel="K" percent={20} />
          </Row>
        </Card>

        <p className="bv-label" style={{ margin: '28px 0 12px' }}>Composed pieces used by the screens</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
          <StatCard icon="printables" color="yellow" value="3" label="Waiting for you" />
          <StatCard icon="print" color="green" value="42" label="Printed this month" />
          <StatCard icon="drop" color="blue" value="$38.20" label="Ink this month" />
          <StatCard icon="users" color="purple" value="18" label="Teachers printing" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginTop: 20 }} role="radiogroup">
          <OptionCard icon="printables" color="blue" title="Teach a topic" subtitle="Math, reading, science, social studies" checked={option === 'teach'} onClick={() => setOption('teach')} />
          <OptionCard icon="sparkle" color="yellow" title="Motivate and inspire" subtitle="Growth mindset, kindness, character" checked={option === 'motivate'} onClick={() => setOption('motivate')} />
          <OptionCard icon="template" color="green" title="Classroom tools" subtitle="Rules, schedules, labels, word walls" checked={option === 'tools'} onClick={() => setOption('tools')} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 20, marginTop: 28 }}>
          <ToolTile color="blue" icon="cursor" title="Drag and drop" subtitle="Drop a file, print it big" />
          <ToolTile color="coral" icon="camera" title="Scan to poster" subtitle="Student work, made big" />
          <ToolTile color="yellow" icon="design" title="Design a poster" subtitle="Blank page or template" />
          <ToolTile color="green" icon="browser" title="Web to poster" subtitle="Print any web page" />
          <ToolTile color="purple" icon="library" title="My design library" subtitle="Everything you made" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 28 }}>
          <Textarea maxLength={500} value={idea} onChange={(e) => setIdea(e.target.value)} />
          <Panel style={{ padding: 24 }}>
            <p className="bv-label">Your poster</p>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              <Tag color="blue">Teach a topic</Tag><Tag color="blue">Grades 3–5</Tag><Tag color="purple">Auto style</Tag><Tag color="yellow">24 × 36 in</Tag>
            </div>
          </Panel>
        </div>
        <div style={{ ...S.wrap, marginTop: 28 }}>
          <CreditsPill credits={10} /><IconButton icon="gear" label="Settings" /><Avatar initials="R12" />
          <Button onClick={() => setModal(true)}>Open a modal</Button>
          <Spinner /><Skeleton width={160} height={18} />
        </div>
        <div style={{ marginTop: 20 }}>
          <Toast inline title="Printing on HP DesignJet T850" subtitle="24 × 36 in · 42%"><div style={{ marginTop: 10 }}><ProgressBar value={42} /></div></Toast>
        </div>
        <Card style={{ marginTop: 20 }}>
          <EmptyState icon="library" title="No designs yet" action={<Button variant="blue" icon="plus">New design</Button>}>Everything you make shows up here, on every computer at your school.</EmptyState>
        </Card>
      </Section>

      <Section n="05" title="Icons" lead="Simple line icons on a 24 px grid, 2.2 px stroke, round ends and corners. Navy on white, white on navy.">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 12 }} data-testid="icons">
          {ICON_NAMES.map((n) => (
            <div key={n} data-icon={n} style={{ background: 'var(--bv-surface)', borderRadius: 14, boxShadow: 'var(--bv-shadow-border)', padding: '16px 0 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
              <Icon name={n} /><span style={{ font: '800 12px var(--bv-font)' }}>{n}</span>
            </div>
          ))}
        </div>
      </Section>

      <Modal open={modal} title="A modal" onClose={() => setModal(false)}>
        <p className="bv-muted">White panel, 28 px radius, window shadow. Escape or the X closes it.</p>
        <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'flex-end' }}>
          <Button onClick={() => setModal(false)}>Cancel</Button><Button variant="green" icon="check" onClick={() => setModal(false)}>Approve</Button>
        </div>
      </Modal>
    </div>
  );
}
